import { initializeApp } from "firebase/app";
import {
  getAuth,
  useDeviceLanguage,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import {
  getMessaging,
  isSupported as messagingIsSupported,
} from "firebase/messaging";
import {
  getAnalytics,
  isSupported as analyticsIsSupported,
  logEvent as analyticsLogEvent,
} from "firebase/analytics";

const config = {
  apiKey: process.env.apiKey,
  authDomain: process.env.authDomain,
  databaseURL: process.env.databaseURL,
  projectId: process.env.projectId,
  storageBucket: process.env.storageBucket,
  messagingSenderId: process.env.messagingSenderId,
  appId: process.env.appId,
  measurementId: process.env.measurementId,
};

const app = initializeApp(config);

const useEmulator =
  import.meta.env.DEV && import.meta.env.MODE === "development";

// Initialize Firestore with persistent cache for all environments
// Use default database across all environments
const db = useEmulator
  ? getFirestore(app)
  : initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });

const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app);
let messaging = null;
messagingIsSupported()
  .then((supported) => {
    if (!supported) return;
    try {
      messaging = getMessaging(app);
      if (useEmulator) window.messaging = messaging;
    } catch (error) {
      console.error("Messaging init failed:", error);
    }
  })
  .catch((error) => {
    console.error("Messaging not supported:", error);
  });
const vapidKey = process.env.vapidKey;

// Analytics: initialize only outside emulator and only when supported by the
// current environment (not supported in SSR, some browsers, etc.). Expose a
// small wrapper with a `logEvent` method to preserve the call-site shape used
// by src/router/index.js and elsewhere.
let analyticsInstance = null;
if (!useEmulator && config.measurementId) {
  analyticsIsSupported()
    .then((supported) => {
      if (!supported) return;
      try {
        analyticsInstance = getAnalytics(app);
      } catch (error) {
        console.error(error);
      }
    })
    .catch((error) => {
      console.error(error);
    });
}
const analytics = {
  logEvent(eventName, params) {
    if (!analyticsInstance) return;
    try {
      analyticsLogEvent(analyticsInstance, eventName, params);
    } catch (error) {
      console.error(error);
    }
  },
};

useDeviceLanguage(auth);
// Use browserLocalPersistence for local storage persistence
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error(error);
});

if (useEmulator) {
  // Emulator connections
  connectAuthEmulator(
    auth,
    `http://${process.env.emulatorIP || "localhost"}:4997`
  );
  connectStorageEmulator(storage, process.env.emulatorIP || "localhost", 4998);
  connectFirestoreEmulator(db, process.env.emulatorIP || "localhost", 4999);
  connectFunctionsEmulator(
    functions,
    process.env.emulatorIP || "localhost",
    5001
  );

  window.auth = auth;
  window.storage = storage;
  window.db = db; // Firestore instance
  window.functions = functions;
  // messaging is set lazily after isSupported check above
}

// Export the service instances
export { analytics, auth, db, storage, functions, messaging, vapidKey };
